import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/Graph/insertDelUndirected.css";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { init } from "i18next";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const ChallengingGraphGame = () => {
    const { darkMode } = useDarkMode();
    const [graph, setGraph] = useState({});
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [currentChallenge, setCurrentChallenge] = useState(0);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [searchResult, setSearchResult] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [availableUsers, setAvailableUsers] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [showHint, setShowHint] = useState(false);
    const [wrongMoves, setWrongMoves] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const userNames = selLanguage
        ? [
            // GR
            "Αλέξης", "Μαρία", "Δημήτρης", "Ελένη", "Νίκος",
            "Σοφία", "Γιάννης", "Κατερίνα", "Πέτρος", "Άννα",
            "Κώστας", "Ιωάννα", "Μιχάλης", "Χριστίνα", "Αντώνης",
            "Βασίλης", "Λήδα", "Στέλιος", "Μαρίνα", "Θάνος"
        ]
        : [
            // EN
            "Alexis", "Maria", "Dimitris", "Eleni", "Nikos",
            "Sophia", "John", "Katerina", "Petros", "Anna",
            "Kostas", "Joanna", "Michael", "Christina", "Antonis",
            "Vasilis", "Lida", "Stelios", "Marina", "Thanos"
        ];


    const challenges = [
        {
            type: "strategic_network",
            title: selLanguage
                ? "Στρατηγικό Δίκτυο"
                : "Strategic Network",
            description: selLanguage
                ? "Δημιούργησε δίκτυο με 5 χρήστες όπου ΚΑΘΕ χρήστης έχει ακριβώς 2 συνδέσεις. Χωρίς απομονωμένους κόμβους!"
                : "Create a network with 5 users where EACH user has exactly 2 connections. No isolated nodes!",
            requiredNodeCount: 5,
            exactDegree: 2,
            mustBeConnected: true,
            hint: selLanguage
                ? "Σκέψου ένα κυκλικό δίκτυο - κάθε χρήστης συνδέεται με τους 2 γείτονές του!"
                : "Think of a circular network – each user connects to their 2 neighbors!"
        },
        {
            type: "bridge_puzzle",
            title: selLanguage
                ? "Η Κρίσιμη Γέφυρα"
                : "The Critical Bridge",
            description: selLanguage
                ? "Δημιούργησε δίκτυο 6 χρηστών σε 2 ομάδες των 3. Οι ομάδες συνδέονται μόνο μέσω ΜΙΑ σύνδεσης (γέφυρα). Αν αφαιρεθεί η γέφυρα, οι ομάδες πρέπει να αποκοπούν."
                : "Create a network of 6 users in 2 groups of 3. The groups are connected by ONLY ONE edge (a bridge). If the bridge is removed, the groups must become disconnected.",
            requiredNodeCount: 6,
            requiresBridge: true,
            twoGroups: true,
            hint: selLanguage
                ? "Χρειάζεσαι 2 ξεχωριστές ομάδες που συνδέονται από μία μόνο σύνδεση!"
                : "You need two separate groups connected by just one link!"
        },
        {
            type: "triangle_network",
            title: selLanguage
                ? "Το Δίκτυο των Τριγώνων"
                : "The Triangle Network",
            description: selLanguage
                ? "Δημιούργησε δίκτυο 6 χρηστών που περιέχει ΑΚΡΙΒΩΣ 4 τρίγωνα (3 χρήστες που συνδέονται μεταξύ τους). Προσοχή: όχι λιγότερα, όχι περισσότερα!"
                : "Create a network of 6 users containing EXACTLY 4 triangles (3 users all connected to each other). Not fewer, not more!",
            requiredNodeCount: 6,
            requiredTriangles: 4,
            hint: selLanguage
                ? "Ένα τρίγωνο = 3 χρήστες που συνδέονται όλοι μεταξύ τους. Πόσες συνδέσεις χρειάζεσαι;"
                : "A triangle = 3 users all connected to each other. How many links do you need?"
        },
        {
            type: "star_vs_line",
            title: selLanguage
                ? "Αστέρι εναντίον Γραμμής"
                : "Star vs Line",
            description: selLanguage
                ? "Δημιούργησε 2 ξεχωριστά δίκτυα: ένα 'αστέρι' (1 κεντρικός + 4 συνδεδεμένοι σε αυτόν) και μια 'γραμμή' (4 χρήστες σε σειρά). Σύνολο 9 χρήστες, χωρίς σύνδεση μεταξύ τους!"
                : "Create 2 separate networks: a 'star' (1 central user + 4 connected to them) and a 'line' (4 users in a row). Total 9 users, with no connections between the two networks!",
            requiredNodeCount: 9,
            requiresStarPattern: true,
            requiresLinePattern: true,
            disconnected: true,
            hint: selLanguage
                ? "Χρειάζεσαι 2 ανεξάρτητα δίκτυα: ένα με 5 χρήστες (αστέρι) και ένα με 4 (γραμμή)"
                : "You need 2 independent networks: one with 5 users (star) and one with 4 (line)"
        },
        {
            type: "diameter_challenge",
            title: selLanguage
                ? "Το Παζλ της Απόστασης"
                : "The Distance Puzzle",
            description: selLanguage
                ? "Δημιούργησε δίκτυο 7 χρηστών όπου η ΜΕΓΙΣΤΗ απόσταση μεταξύ οποιωνδήποτε 2 χρηστών είναι ΑΚΡΙΒΩΣ 3 βήματα. Όχι λιγότερο, όχι περισσότερο!"
                : "Create a network of 7 users where the MAXIMUM distance between any two users is EXACTLY 3 steps. Not less, not more!",
            requiredNodeCount: 7,
            exactDiameter: 3,
            hint: selLanguage
                ? "Διάμετρος = η μεγαλύτερη απόσταση στο δίκτυο. Θέλεις κάποιους να απέχουν 3 βήματα αλλά όχι περισσότερο!"
                : "Diameter = the largest distance in the network. You need some users to be 3 steps apart, but not more!"
        },
        {
            type: "complex_conditions",
            title: selLanguage
                ? "Το Τελικό Αίνιγμα"
                : "The Final Riddle",
            description: selLanguage
                ? "Δημιούργησε δίκτυο 8 χρηστών με: (1) Κάθε χρήστης έχει 3-4 συνδέσεις, (2) Υπάρχουν τουλάχιστον 6 τρίγωνα, (3) Δεν υπάρχει κανένας χρήστης που αν αφαιρεθεί να σπάσει το δίκτυο σε κομμάτια."
                : "Create a network of 8 users where: (1) Each user has 3–4 connections, (2) There are at least 6 triangles, (3) There is no user whose removal would break the network into pieces.",
            requiredNodeCount: 8,
            degreeRange: [3, 4],
            minTriangles: 6,
            noArticulationPoints: true,
            hint: selLanguage
                ? "Χρειάζεσαι ένα πυκνό, καλά συνδεδεμένο δίκτυο όπου κανένας χρήστης δεν είναι απαραίτητος!"
                : "You need a dense, well-connected network where no single user is essential!"
        }
    ];


    const SVG_WIDTH = 700;
    const SVG_HEIGHT = 466;
    const NODE_SIZE = 45;

    useEffect(() => {
        initializeGame();
    }, []);

    const initializeGame = () => {
        setGraph({});
        setNodes([]);
        setEdges([]);
        setSelectedNodes([]);
        setCurrentChallenge(0);
        setScore(0);
        setLives(3);
        setAvailableUsers([...userNames]);
        setUserInput("");
        setWrongMoves(0);
        setShowHint(false);
        setShowGameOver(false);
        setShowSuccess(false);
        setShowError(false);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const clearCanvas = () => {
        setGraph({});
        setNodes([]);
        setEdges([]);
        setSelectedNodes([]);
        setAvailableUsers([...userNames]);
        setUserInput("");
    }

    const getNodePosition = (index, total) => {
        const centerX = SVG_WIDTH / 2;
        const centerY = SVG_HEIGHT / 2;
        const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) * 0.38;

        if (total === 1) {
            return { x: centerX - NODE_SIZE / 2, y: centerY - NODE_SIZE / 2 };
        }

        const angle = (2 * Math.PI * index) / total;
        return {
            x: centerX + radius * Math.cos(angle) - NODE_SIZE / 2,
            y: centerY + radius * Math.sin(angle) - NODE_SIZE / 2
        };
    };

    const addNode = (nodeName) => {
        if (graph[nodeName]) {
            showErrorMessage(selLanguage ? "Ο χρήστης υπάρχει ήδη!" : "User already exists!");
            return false;
        }

        const newGraph = { ...graph, [nodeName]: [] };
        const nodeList = Object.keys(newGraph);
        const newNodes = nodeList.map((name, index) => ({
            id: name,
            label: name,
            ...getNodePosition(index, nodeList.length)
        }));

        setGraph(newGraph);
        setNodes(newNodes);
        setAvailableUsers(availableUsers.filter(user => user !== nodeName));
        return true;
    };

    const addEdge = (node1, node2) => {
        if (!graph[node1] || !graph[node2]) {
            showErrorMessage(selLanguage ? "Και οι δύο χρήστες πρέπει να υπάρχουν!" : "Both users must exist!");
            return false;
        }

        if (node1 === node2) {
            showErrorMessage(selLanguage ? "Δεν μπορείς να συνδέσεις κάποιον με τον εαυτό του!" : "Cannot connect a user to themselves!");
            return false;
        }

        if (graph[node1].includes(node2)) {
            showErrorMessage(selLanguage ? "Η σύνδεση υπάρχει ήδη!" : "Connection already exists!");
            return false;
        }

        const newGraph = { ...graph };
        newGraph[node1] = [...newGraph[node1], node2];
        newGraph[node2] = [...newGraph[node2], node1];

        const newEdges = [];
        Object.keys(newGraph).forEach(node => {
            newGraph[node].forEach(neighbor => {
                if (node < neighbor) {
                    newEdges.push({ from: node, to: neighbor });
                }
            });
        });

        setGraph(newGraph);
        setEdges(newEdges);
        return true;
    };

    const removeEdge = (node1, node2) => {
        if (!graph[node1] || !graph[node2]) {
            showErrorMessage(selLanguage ? "Οι χρήστες δεν υπάρχουν!" : "Users do not exist!");
            return false;
        }

        if (!graph[node1].includes(node2)) {
            showErrorMessage(selLanguage ? "Δεν υπάρχει σύνδεση μεταξύ τους!" : "No connection exists between them!");
            return false;
        }

        const newGraph = { ...graph };
        newGraph[node1] = newGraph[node1].filter(n => n !== node2);
        newGraph[node2] = newGraph[node2].filter(n => n !== node1);

        const newEdges = [];
        Object.keys(newGraph).forEach(node => {
            newGraph[node].forEach(neighbor => {
                if (node < neighbor) {
                    newEdges.push({ from: node, to: neighbor });
                }
            });
        });

        setGraph(newGraph);
        setEdges(newEdges);
        return true;
    };

    const bfsSearch = (start, target) => {
        if (!graph[start] || !graph[target]) return null;
        if (start === target) return [start];

        const visited = new Set();
        const queue = [[start]];

        while (queue.length > 0) {
            const path = queue.shift();
            const current = path[path.length - 1];

            if (current === target) {
                return path;
            }

            if (!visited.has(current)) {
                visited.add(current);
                graph[current].forEach(neighbor => {
                    if (!visited.has(neighbor)) {
                        queue.push([...path, neighbor]);
                    }
                });
            }
        }
        return null;
    };


    const findBridges = (g) => {
        const bridges = [];
        Object.keys(g).forEach(node => {
            g[node].forEach(neighbor => {
                if (node < neighbor) {
                    const tempGraph = JSON.parse(JSON.stringify(g));
                    tempGraph[node] = tempGraph[node].filter(n => n !== neighbor);
                    tempGraph[neighbor] = tempGraph[neighbor].filter(n => n !== node);

                    const components = countConnectedComponents(tempGraph);
                    if (components > 1) {
                        bridges.push([node, neighbor]);
                    }
                }
            });
        });
        return bridges;
    };

    const countTriangles = (g) => {
        let count = 0;
        const nodes = Object.keys(g);
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                for (let k = j + 1; k < nodes.length; k++) {
                    if (g[nodes[i]].includes(nodes[j]) &&
                        g[nodes[j]].includes(nodes[k]) &&
                        g[nodes[k]].includes(nodes[i])) {
                        count++;
                    }
                }
            }
        }
        return count;
    };

    const countConnectedComponents = (g) => {
        const visited = new Set();
        let components = 0;

        const dfs = (node) => {
            visited.add(node);
            (g[node] || []).forEach(neighbor => {
                if (!visited.has(neighbor)) {
                    dfs(neighbor);
                }
            });
        };

        Object.keys(g).forEach(node => {
            if (!visited.has(node)) {
                components++;
                dfs(node);
            }
        });

        return components;
    };

    const hasStarPattern = (g) => {
        return Object.keys(g).some(center => {
            const leaves = g[center].filter(neighbor => g[neighbor].length === 1);
            return leaves.length === 4 && g[center].length === 4;
        });
    };

    const hasLinePattern = (g) => {
        const nodes = Object.keys(g);
        for (let i = 0; i < nodes.length; i++) {
            const visited = new Set();
            const path = [];

            const findPath = (node) => {
                visited.add(node);
                path.push(node);

                if (path.length === 4) {
                    const isLine = path.every((n, idx) => {
                        if (idx === 0) return g[n].length === 1;
                        if (idx === path.length - 1) return g[n].length === 1;
                        return g[n].length === 2;
                    });
                    return isLine;
                }

                for (const neighbor of g[node]) {
                    if (!visited.has(neighbor)) {
                        if (findPath(neighbor)) return true;
                    }
                }

                path.pop();
                return false;
            };

            if (g[nodes[i]].length === 1 && findPath(nodes[i])) {
                return true;
            }
        }
        return false;
    };

    const calculateDiameter = (g) => {
        const nodes = Object.keys(g);
        if (nodes.length === 0) return null;

        let maxDistance = 0;
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const path = bfsSearch(nodes[i], nodes[j]);
                if (path === null) return null;
                maxDistance = Math.max(maxDistance, path.length - 1);
            }
        }
        return maxDistance;
    };

    const checkDegreeRange = (g, range) => {
        return Object.values(g).every(neighbors =>
            neighbors.length >= range[0] && neighbors.length <= range[1]
        );
    };

    const hasArticulationPoint = (g) => {
        const nodes = Object.keys(g);
        for (const node of nodes) {
            const tempGraph = { ...g };
            delete tempGraph[node];
            Object.keys(tempGraph).forEach(n => {
                tempGraph[n] = tempGraph[n].filter(neighbor => neighbor !== node);
            });

            if (countConnectedComponents(tempGraph) > 1) {
                return true;
            }
        }
        return false;
    };

    const checkChallenge = () => {
        const challenge = challenges[currentChallenge];
        let success = false;
        let feedback = "";

        switch (challenge.type) {
            case "strategic_network":
                const nodeCount = Object.keys(graph).length;
                const correctCount = nodeCount === challenge.requiredNodeCount;
                const allHaveExactDegree = Object.values(graph).every(neighbors =>
                    neighbors.length === challenge.exactDegree
                );

                const isConnected = nodeCount > 0 && bfsSearch(
                    Object.keys(graph)[0],
                    Object.keys(graph)[nodeCount - 1]
                ) !== null;

                if (correctCount && allHaveExactDegree && isConnected) {
                    success = true;
                    feedback = selLanguage ? "Τέλειο! Στρατηγικό δίκτυο!" : " Perfect! Strategic network!";
                } else if (!correctCount) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες, έχεις ${nodeCount}!` : `You need ${challenge.requiredNodeCount} users, you have ${nodeCount}!`;
                } else if (!allHaveExactDegree) {
                    feedback = selLanguage ? "Κάθε χρήστης πρέπει να έχει ΑΚΡΙΒΩΣ 2 συνδέσεις!" : "Each user must have EXACTLY 2 connections!";
                } else {
                    feedback = selLanguage ? "Το δίκτυο δεν είναι συνεχόμενο!" : "The network is not connected!";
                }
                break;

            case "bridge_puzzle":
                const hasCorrectNodes = Object.keys(graph).length === challenge.requiredNodeCount;

                // Find bridges
                const bridges = findBridges(graph);
                const hasOneBridge = bridges.length === 1;

                if (hasCorrectNodes && hasOneBridge) {
                    success = true;
                    feedback = selLanguage ? "Εξαιρετικά! Βρήκες τη γέφυρα!" : " Excellent! You found the bridge!";
                } else if (!hasCorrectNodes) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες!` : `You need ${challenge.requiredNodeCount} users!`;
                } else if (bridges.length === 0) {
                    feedback = selLanguage ? "Δεν υπάρχει γέφυρα! Πρέπει να υπάρχει ΜΙΑ κρίσιμη σύνδεση!" : "You have no bridges! There must be ONE critical connection!";
                } else {
                    feedback = selLanguage ? `Βρέθηκαν ${bridges.length} γέφυρες, χρειάζεται ακριβώς 1!` : ` Found ${bridges.length} bridges, exactly 1 is needed!`;
                }
                break;

            case "triangle_network":
                const hasNodes = Object.keys(graph).length === challenge.requiredNodeCount;
                const triangles = countTriangles(graph);

                if (hasNodes && triangles === challenge.requiredTriangles) {
                    success = true;
                    feedback = selLanguage ? "Απίστευτο! Ακριβώς 4 τρίγωνα!" : " Incredible! Exactly 4 triangles!";
                } else if (!hasNodes) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες!` : `You need ${challenge.requiredNodeCount} users!`;
                } else {
                    feedback = selLanguage ? `Έχεις ${triangles} τρίγωνα, χρειάζεσαι ${challenge.requiredTriangles}!` : ` You have ${triangles} triangles, you need ${challenge.requiredTriangles}!`;
                }
                break;

            case "star_vs_line":
                const totalNodes = Object.keys(graph).length;
                const hasStar = hasStarPattern(graph);
                const hasLine = hasLinePattern(graph);
                const components = countConnectedComponents(graph);

                if (totalNodes === challenge.requiredNodeCount && hasStar && hasLine && components === 2) {
                    success = true;
                    feedback = selLanguage ? "Φανταστικό! Δύο ξεχωριστά μοτίβα!" : " Fantastic! Two distinct patterns!";
                } else if (totalNodes !== challenge.requiredNodeCount) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες συνολικά!` : ` You need ${challenge.requiredNodeCount} users total!`;
                } else if (components !== 2) {
                    feedback = selLanguage ? "Πρέπει να έχεις 2 ΞΕΧΩΡΙΣΤΑ δίκτυα!" : " You must have 2 SEPARATE networks!";
                } else if (!hasStar) {
                    feedback = selLanguage ? "Δεν βρέθηκε το μοτίβο αστεριού (1 κεντρικός + 4 συνδεδεμένοι)!" : " Star pattern not found (1 central + 4 connected)!";
                } else {
                    feedback = selLanguage ? "Δεν βρέθηκε το μοτίβο γραμμής (4 χρήστες σε σειρά)!" : " Line pattern not found (4 users in a row)!";
                }
                break;

            case "diameter_challenge":
                const hasCorrectNodeCount = Object.keys(graph).length === challenge.requiredNodeCount;
                const diameter = calculateDiameter(graph);

                if (hasCorrectNodeCount && diameter === challenge.exactDiameter) {
                    success = true;
                    feedback = selLanguage ? "Εκπληκτικό! Ακριβής διάμετρος 3!" : " Amazing! Exact diameter of 3!";
                } else if (!hasCorrectNodeCount) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες!` : ` You need ${challenge.requiredNodeCount} users!`;
                } else if (diameter === null) {
                    feedback = selLanguage ? "Το δίκτυο δεν είναι συνεχόμενο!" : " The network is not connected!";
                } else {
                    feedback = selLanguage ? `Η διάμετρος είναι ${diameter}, χρειάζεται ΑΚΡΙΒΩΣ ${challenge.exactDiameter}!` : ` The diameter is ${diameter}, EXACTLY ${challenge.exactDiameter} is needed!`;
                }
                break;

            case "complex_conditions":
                const finalNodeCount = Object.keys(graph).length;
                const degreeCheck = checkDegreeRange(graph, challenge.degreeRange);
                const triangleCount = countTriangles(graph);
                const noArticulation = !hasArticulationPoint(graph);

                if (finalNodeCount === challenge.requiredNodeCount && degreeCheck &&
                    triangleCount >= challenge.minTriangles && noArticulation) {
                    success = true;
                    feedback = selLanguage ? "ΑΠΙΣΤΕΥΤΟ! Έλυσες το τελικό αίνιγμα!" : " INCREDIBLE! You solved the final riddle!";
                } else if (finalNodeCount !== challenge.requiredNodeCount) {
                    feedback = selLanguage ? `Χρειάζεσαι ${challenge.requiredNodeCount} χρήστες!` : ` You need ${challenge.requiredNodeCount} users!`;
                } else if (!degreeCheck) {
                    feedback = selLanguage ? "Κάθε χρήστης πρέπει να έχει 3-4 συνδέσεις!" : " Each user must have 3-4 connections!";
                } else if (triangleCount < challenge.minTriangles) {
                    feedback = selLanguage ? `Έχεις ${triangleCount} τρίγωνα, χρειάζεσαι τουλάχιστον ${challenge.minTriangles}!` : ` You have ${triangleCount} triangles, at least ${challenge.minTriangles} are needed!`;
                } else {
                    feedback = selLanguage ? "Υπάρχει χρήστης που αν αφαιρεθεί σπάει το δίκτυο!" : " There is a user whose removal breaks the network!";
                }
                break;
        }

        if (success) {
            if (currentChallenge === 6) {
                setShowGameWon(true);
            }
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                if (currentChallenge < challenges.length - 1) {
                    setCurrentChallenge(currentChallenge + 1);
                    setShowHint(false);
                    setWrongMoves(0);
                } else {
                    setShowGameOver(true);
                }
            }, 2500);
        } else {
            const newLives = lives - 1;
            setLives(newLives);
            if (newLives === 0) {
                setShowGameOver(true);
            }
            showErrorMessage(feedback);
            setWrongMoves(wrongMoves + 1);
        }

        setSelectedNodes([]);
    };

    const handleNodeClick = (nodeId) => {
        if (selectedNodes.includes(nodeId)) {
            setSelectedNodes(selectedNodes.filter(id => id !== nodeId));
        } else if (selectedNodes.length < 2) {
            setSelectedNodes([...selectedNodes, nodeId]);
        } else {
            setSelectedNodes([nodeId]);
        }
    };

    const handleAddUser = () => {
        if (!userInput.trim()) {
            showErrorMessage(selLanguage ? "Επέλεξε έναν χρήστη!" : "Select a user!");
            return;
        }

        const success = addNode(userInput.trim());
        if (success) {
            setUserInput("");
        }
    };

    const handleCreateConnection = () => {
        if (selectedNodes.length !== 2) {
            showErrorMessage(selLanguage ? "Επέλεξε ακριβώς 2 χρήστες για σύνδεση!" : "Select exactly 2 users to connect!");
            return;
        }

        addEdge(selectedNodes[0], selectedNodes[1]);
        setSelectedNodes([]);
    };

    const handleRemoveConnection = () => {
        if (selectedNodes.length !== 2) {
            showErrorMessage(selLanguage ? "Επέλεξε ακριβώς 2 χρήστες για διαγραφή σύνδεσης!" : "Select exactly 2 users to remove connection!");
            return;
        }

        removeEdge(selectedNodes[0], selectedNodes[1]);
        setSelectedNodes([]);
    };

    const showErrorMessage = (message) => {
        setErrorMessage(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
    };

    const getNodeById = (id) => nodes.find(n => n.id === id);
    const currentChallengeData = challenges[currentChallenge];

    return (
        <div
            className="insertDelUndirectedContainer"
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
            }}
        >

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={initializeGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={initializeGame}

            />
            {/* Lives Display */}
            <div className="insertDelUndirectedLivesContainer">
                <div style={{
                    color: darkMode ? "white" : "#1e293b",
                    padding: "12px 24px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "18px",
                    border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                }}>
                    {selLanguage ? "Επίπεδο" : "Level"} {currentChallenge + 1} / {challenges.length}
                </div>
                <div className="insertDelUndirectedLivesRight">

                    <div className="insertDelUndirectedHeartsWrapper">
                        {[1, 2, 3].map((heartNum) => (
                            <div
                                key={heartNum}
                                className={`insertDelUndirectedHeart ${lives >= heartNum ? 'active' : 'inactive'}`}
                            >
                                ❤️
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/* Challenge Description */}
            <div className="insertDelUndirectedProm">

                <div className="insertDelUndirectedPromDescription">{currentChallengeData?.description}</div>
            </div>

            {/* Game Frame */}
            <div className="insertDelUndirectedFrame">
                <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="insertDelUndirectedSvg"
                >
                    {/* Edges */}
                    {edges.map((edge, idx) => {
                        const from = getNodeById(edge.from);
                        const to = getNodeById(edge.to);
                        if (!from || !to) return null;

                        const fromCenterX = from.x + NODE_SIZE / 2;
                        const fromCenterY = from.y + NODE_SIZE / 2;
                        const toCenterX = to.x + NODE_SIZE / 2;
                        const toCenterY = to.y + NODE_SIZE / 2;

                        return (
                            <motion.line
                                key={`${edge.from}-${edge.to}`}
                                x1={fromCenterX}
                                y1={fromCenterY}
                                x2={toCenterX}
                                y2={toCenterY}
                                stroke="#10b981"
                                strokeWidth="3"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.8, delay: idx * 0.1 }}
                            />
                        );
                    })}

                    {/* Nodes */}
                    {nodes.map((node, index) => (
                        <motion.g
                            key={node.id}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 20 }}
                            onClick={() => handleNodeClick(node.id)}
                            className="insertDelUndirectedNodeGroup"
                        >
                            <circle
                                cx={node.x + NODE_SIZE / 2 + 2}
                                cy={node.y + NODE_SIZE / 2 + 2}
                                r={NODE_SIZE / 2}
                                fill="rgba(0,0,0,0.15)"
                            />

                            <circle
                                cx={node.x + NODE_SIZE / 2}
                                cy={node.y + NODE_SIZE / 2}
                                r={NODE_SIZE / 2}
                                className={`insertDelUndirectedNode ${selectedNodes.includes(node.id) ? 'selected' : ''}`}
                            />

                            <text
                                x={node.x + NODE_SIZE / 2}
                                y={node.y + NODE_SIZE / 2 - 5}
                                textAnchor="middle"
                                fill="white"
                                fontSize="20"
                            >
                                👤
                            </text>

                            <text
                                x={node.x + NODE_SIZE / 2}
                                y={node.y + NODE_SIZE + 20}
                                textAnchor="middle"
                                fill="#1e293b"
                                fontSize="12"
                                fontWeight="600"
                            >
                                {node.label}
                            </text>
                        </motion.g>
                    ))}
                </svg>
            </div>

            {showError && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#fee2e2',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #dc2626',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >

                    <p style={{ color: '#991b1b', margin: 0 }}>
                        {errorMessage}
                    </p>
                </motion.div>
            )}

            {/* Success Message */}
            {showSuccess && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#d1fae5',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #10b981',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >
                    <p style={{ color: '#065f46', margin: 0 }}>
                        {selLanguage ? "Συγχαρητήρια! Προχωράς στο επόμενο επίπεδο!" : "Congratulations! Moving to the next level!"}
                    </p>
                </motion.div>
            )}

            {/* User Selection */}
            <div className="insertDelUndirectedLeftNodes">
                <select
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    className="insertDelUndirectedUserSelect"
                >
                    <option value="">{selLanguage ? "Επέλεξε χρήστη..." : "Select user..."}
                    </option>
                    {availableUsers.map(user => (
                        <option key={user} value={user}>{user}</option>
                    ))}
                </select>
                <button
                    onClick={handleAddUser}
                    className="insertDelUndirectedAddBtn"
                    disabled={!userInput}
                >
                    {selLanguage ? "Προσθήκη" : "Add"}
                </button>
            </div>

            {/* Control Buttons */}
            <div className="insertDelUndirectedControls">

                <motion.button
                    onClick={checkChallenge}
                    className="insertDelUndirectedCheckBtn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {selLanguage ? "Έλεγχος Λύσης" : "Check solution"}
                </motion.button>


                <button
                    onClick={handleCreateConnection}
                    className="insertDelUndirectedConnectBtn"
                    disabled={selectedNodes.length !== 2}
                >
                    {selLanguage ? "Σύνδεση" : "Connect"} ({selectedNodes.length}/2)
                </button>

                <button
                    onClick={handleRemoveConnection}
                    className="insertDelUndirectedDisconnectBtn"
                    disabled={selectedNodes.length !== 2}
                >
                    {selLanguage ? "Διαγραφή Σύνδεσης" : "Remove connection"}
                </button>

                <motion.button
                    onClick={clearCanvas}
                    className="insertDelUndirectedResetBtn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {selLanguage ? "Καθαρισμός" : "Clear"}
                </motion.button>
            </div>

            {/* Selected Users Info */}
            {selectedNodes.length > 0 && (
                <div className="insertDelUndirectedSelectedInfo">
                    Επιλεγμένοι: {selectedNodes.join(", ")}
                </div>
            )}


            {/* Hint Display */}
            <AnimatePresence>
                {showHint && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="insertDelUndirectedHintBox"
                    >
                        💡 <strong>{selLanguage ? "Βοήθεια:" : "Hint:"}
                        </strong> {currentChallengeData?.hint}
                    </motion.div>
                )}
            </AnimatePresence>


        </div>
    );
};

export default ChallengingGraphGame;